import type { PartDef } from "./types";
import { PART_BY_ID as PARTS_LOOKUP } from "./PartCatalog";

/** A placed part within a rocket design.
 *  Position is in side-view meters relative to the pad origin (y up, x right). */
export interface PlacedPart {
  /** Unique instance id within the rocket. */
  uid: string;
  partId: string;
  x: number;
  y: number;
  /** Rotation in degrees around the builder Z axis (only meaningful for a few parts). */
  rotation: number;
  /** If mirrored (e.g. side-mounted fins on the right). */
  mirror: boolean;
  /** Stage this part fires/separates with. Lower numbers fire first (stage 0 = lift-off). */
  stage: number;
  /** Current fuel in kg, if applicable. Used at runtime. */
  fuel?: number;
}

export interface RocketDesign {
  id: string;
  name: string;
  updatedAt: number;
  parts: PlacedPart[];
}

/** Result of structural validation. */
export interface ValidationResult {
  ok: boolean;
  /** Hard errors — prevent launch. */
  errors: string[];
  /** Soft warnings — launch allowed but flagged. */
  warnings: string[];
}

const CONNECT_EPS = 0.08; // metres — node positions within this are considered connected

/** Build the adjacency graph of placed parts based on attach nodes. */
export function buildConnectivity(
  design: RocketDesign,
  partDef: (id: string) => PartDef,
): Map<string, Set<string>> {
  const adj = new Map<string, Set<string>>();
  for (const p of design.parts) adj.set(p.uid, new Set());

  const items = design.parts.map((p) => {
    const def = partDef(p.partId);
    return {
      p,
      def,
      nodes: def.nodes.map((n) => ({ ...n, worldX: p.x + n.x, worldY: p.y + n.y })),
    };
  });

  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const a = items[i], b = items[j];
      for (const na of a.nodes) {
        for (const nb of b.nodes) {
          // Stack nodes only connect to stack nodes; surface nodes can meet stack or surface.
          if (na.kind === "stack" && nb.kind !== "stack") continue;
          if (na.kind !== "stack" && nb.kind === "stack") continue;
          const d = Math.hypot(na.worldX - nb.worldX, na.worldY - nb.worldY);
          if (d <= CONNECT_EPS) {
            adj.get(a.p.uid)!.add(b.p.uid);
            adj.get(b.p.uid)!.add(a.p.uid);
          }
        }
      }
    }
  }
  return adj;
}

/** Connected component containing a seed uid. */
export function connectedComponent(
  seed: string,
  adj: Map<string, Set<string>>,
): Set<string> {
  const seen = new Set<string>();
  const stack = [seed];
  while (stack.length) {
    const cur = stack.pop()!;
    if (seen.has(cur)) continue;
    seen.add(cur);
    const neighbors = adj.get(cur);
    if (neighbors) for (const n of neighbors) if (!seen.has(n)) stack.push(n);
  }
  return seen;
}

/** Run all structural checks and return friendly messages a 9–12 year old can act on. */
export function validateDesign(
  design: RocketDesign,
  partDef: (id: string) => PartDef,
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (design.parts.length === 0) {
    return { ok: false, errors: ["Your rocket is empty. Drag some parts onto the pad."], warnings };
  }

  const commandParts = design.parts.filter((p) => partDef(p.partId).isControl);
  if (commandParts.length === 0) {
    errors.push("Add a Mk1 Capsule or Probe Core — something has to steer the rocket.");
  }

  const engineParts = design.parts.filter((p) => (partDef(p.partId).thrust ?? 0) > 0);
  if (engineParts.length === 0) {
    errors.push("Add an engine or booster — nothing is producing thrust.");
  }

  if (commandParts.length === 0 || engineParts.length === 0) {
    return { ok: false, errors, warnings };
  }

  // Connectivity: every part should be reachable from the root command part.
  const adj = buildConnectivity(design, partDef);
  const root = commandParts[0];
  const reachable = connectedComponent(root.uid, adj);

  const floating = design.parts.filter((p) => !reachable.has(p.uid));
  if (floating.length > 0) {
    const names = floating
      .slice(0, 3)
      .map((p) => partDef(p.partId).name)
      .join(", ");
    errors.push(
      `${floating.length} part${floating.length === 1 ? " isn't" : "s aren't"} connected to your rocket (${names}${floating.length > 3 ? "…" : ""}). Drag them onto an attach point so they touch a node.`,
    );
  }

  // Each engine must be reachable from the command part, AND (for liquid engines) share a
  // connected component with a liquid-fuel tank. Solid boosters carry their own fuel.
  for (const e of engineParts) {
    if (!reachable.has(e.uid)) {
      errors.push(`${partDef(e.partId).name} isn't connected to the command pod — the rocket can't fire it.`);
      continue;
    }
    const def = partDef(e.partId);
    if (def.fuelKind === "solid") continue; // self-contained
    // Liquid engine: search its own connected component for any liquid tank
    const comp = connectedComponent(e.uid, adj);
    const hasLiquidTank = [...comp].some((uid) => {
      const pid = design.parts.find((p) => p.uid === uid)!.partId;
      const d = partDef(pid);
      return d.fuelKind === "liquid" && (d.fuelCapacity ?? 0) > 0;
    });
    if (!hasLiquidTank) {
      errors.push(`${def.name} has no fuel tank connected to it. Put a Liquid Tank between the capsule and the engine.`);
    }
  }

  // Warnings (non-blocking)
  const hasChute = design.parts.some((p) => (partDef(p.partId).chuteDragMult ?? 0) > 0);
  const crewed = commandParts.some((p) => partDef(p.partId).id.startsWith("pod"));
  if (crewed && !hasChute) {
    warnings.push("Your crew capsule has no parachute — re-entry will be rough. Add one unless you're just testing.");
  }

  // Warn if engine is pointing the wrong way (node at top instead of bottom = "upside down")
  for (const e of engineParts) {
    const def = partDef(e.partId);
    const stackNodes = def.nodes.filter((n) => n.kind === "stack");
    if (stackNodes.length === 1 && stackNodes[0].y > 0.01) {
      // engine only has a top node — find whatever is above and verify it isn't above in world coords
      const above = design.parts.find(
        (p) =>
          p.uid !== e.uid &&
          Math.abs(p.x - e.x) < 0.5 &&
          p.y > e.y + def.height - CONNECT_EPS,
      );
      if (!above) warnings.push(`${def.name} has nothing attached to its top — did you mean to flip it?`);
    }
  }

  return { ok: errors.length === 0, errors, warnings };
}

/** Attempt to auto-resolve validation errors by adding missing parts and
 *  re-snapping floating ones. Mutates `design` in place. Returns a list of
 *  human-readable changes for the UI. Best-effort; stops iterating when it
 *  can't make further progress.
 *
 *  Strategies, in order of priority each iteration:
 *   1. Empty rocket  → add Capsule + Tank + Engine.
 *   2. No command     → add a Mk1 Capsule at the top of the stack.
 *   3. Floating parts → re-snap them under the lowest reachable part.
 *   4. No engine      → add an Atlas Liquid Engine under the lowest stack-node part.
 *   5. Liquid engine with no fuel → insert a Liquid Tank (S) between engine and stack.
 */
export function autoFixDesign(
  design: RocketDesign,
  partDef: (id: string) => PartDef,
): { changes: string[] } {
  const changes: string[] = [];

  // Case 1 — completely empty: build a starter rocket from scratch.
  if (design.parts.length === 0) {
    const capsule = partDef("pod-mk1");
    const tank = partDef("tank-lfo-s");
    const engine = partDef("engine-lfo");
    // Engine bottom at y=0.3 (slight pad clearance). Stack upward.
    const engineY = 0.3;
    const tankY = engineY + engine.height;
    const capsuleY = tankY + tank.height;
    design.parts.push(makePlaced("engine-lfo", 0, engineY, 0));
    design.parts.push(makePlaced("tank-lfo-s", 0, tankY, 0));
    design.parts.push(makePlaced("pod-mk1", 0, capsuleY, 0));
    changes.push("Built a starter rocket: capsule + fuel tank + engine");
  }

  for (let iter = 0; iter < 8; iter++) {
    const v = validateDesign(design, partDef);
    if (v.ok) break;

    let progressed = false;
    const adj = buildConnectivity(design, partDef);

    // Case 2 — no command pod → add one on top of the highest part.
    const hasControl = design.parts.some((p) => partDef(p.partId).isControl);
    if (!hasControl) {
      const top = highestStackPoint(design, partDef);
      const capsule = partDef("pod-mk1");
      const capsuleBottomNode = capsule.nodes.find((n) => n.kind === "stack" && n.y < 0.0001);
      const y = top.wy - (capsuleBottomNode?.y ?? 0);
      design.parts.push(makePlaced("pod-mk1", top.wx, y, 0));
      changes.push("Added a Mk1 Capsule on top of the stack");
      progressed = true;
      continue;
    }

    // Case 3 — floating parts → re-snap under the lowest reachable part.
    const commandPart = design.parts.find((p) => partDef(p.partId).isControl)!;
    const reachable = connectedComponent(commandPart.uid, adj);
    const floating = design.parts.filter((p) => !reachable.has(p.uid));
    if (floating.length > 0) {
      // Snap each floating part to the *bottom* of the lowest reachable stack part.
      for (const f of floating) {
        const def = partDef(f.partId);
        const mainParts = design.parts.filter((p) => reachable.has(p.uid));
        const lowest = mainParts.reduce((a, b) => (a.y < b.y ? a : b));
        const lowestDef = partDef(lowest.partId);
        const hostBottomNode = lowestDef.nodes.find((n) => n.kind === "stack" && n.y < 0.0001);
        const myTopNode = def.nodes.find((n) => n.kind === "stack" && n.y > 0.0001);
        if (hostBottomNode && myTopNode) {
          f.x = lowest.x + hostBottomNode.x - myTopNode.x;
          f.y = lowest.y + hostBottomNode.y - myTopNode.y;
          changes.push(`Reconnected ${def.name}`);
          progressed = true;
        } else {
          // Surface-mount fallback: park it against the side of the capsule.
          const surfaceNode = lowestDef.nodes.find((n) => n.kind === "surface");
          const mySurfaceNode = def.nodes.find((n) => n.kind === "surface");
          if (surfaceNode && mySurfaceNode) {
            f.x = lowest.x + surfaceNode.x - mySurfaceNode.x;
            f.y = lowest.y + surfaceNode.y - mySurfaceNode.y;
            changes.push(`Surface-attached ${def.name}`);
            progressed = true;
          }
        }
      }
      if (progressed) continue;
    }

    // Case 4 — no engine → add one to the bottom of the stack.
    const hasEngine = design.parts.some((p) => (partDef(p.partId).thrust ?? 0) > 0);
    if (!hasEngine) {
      const bot = lowestStackPoint(design, partDef);
      const engine = partDef("engine-lfo");
      const engineTopNode = engine.nodes.find((n) => n.kind === "stack")!;
      const y = bot.wy - engineTopNode.y;
      design.parts.push(makePlaced("engine-lfo", bot.wx, y, 0));
      changes.push("Added an Atlas Liquid Engine at the base");
      progressed = true;
      continue;
    }

    // Case 5 — liquid engine without tank in its component → insert a tank.
    const newAdj = buildConnectivity(design, partDef);
    const liquidEngines = design.parts.filter((p) => {
      const d = partDef(p.partId);
      return (d.thrust ?? 0) > 0 && d.fuelKind !== "solid";
    });
    for (const e of liquidEngines) {
      const comp = connectedComponent(e.uid, newAdj);
      const hasTank = [...comp].some((uid) => {
        const part = design.parts.find((p) => p.uid === uid)!;
        return partDef(part.partId).fuelKind === "liquid";
      });
      if (hasTank) continue;
      // Engine top node in world coords
      const eDef = partDef(e.partId);
      const eTop = eDef.nodes.find((n) => n.kind === "stack")!;
      const eTopWx = e.x + eTop.x;
      const eTopWy = e.y + eTop.y;
      // Push everything whose bottom is at or above the engine top up by tank.height
      const tankDef = partDef("tank-lfo-s");
      const dy = tankDef.height;
      for (const p of design.parts) {
        if (p.uid === e.uid) continue;
        if (p.y + 0.01 >= eTopWy - 0.01) p.y += dy;
      }
      // Place tank so its bottom node sits on the engine's top node.
      const tankBottomNode = tankDef.nodes.find((n) => n.kind === "stack" && n.y < 0.0001)!;
      design.parts.push(
        makePlaced("tank-lfo-s", eTopWx - tankBottomNode.x, eTopWy - tankBottomNode.y, 0),
      );
      changes.push(`Inserted a Liquid Tank between the ${eDef.name} and the rocket`);
      progressed = true;
      break; // re-run validation after a structural change
    }
    if (!progressed) break; // give up — nothing we can automate
  }

  design.updatedAt = Date.now();
  return { changes };
}

function makePlaced(partId: string, x: number, y: number, stage: number): PlacedPart {
  const d = PARTS_LOOKUP.get(partId);
  return {
    uid: crypto.randomUUID(),
    partId,
    x,
    y,
    rotation: 0,
    mirror: false,
    stage,
    fuel: d?.fuelCapacity,
  };
}

/** Highest world point of any attached stack node (where a command pod would go). */
function highestStackPoint(design: RocketDesign, partDef: (id: string) => PartDef) {
  let best: { wx: number; wy: number } | undefined;
  for (const p of design.parts) {
    const d = partDef(p.partId);
    for (const n of d.nodes) {
      if (n.kind !== "stack") continue;
      if (n.y < 0.5 * d.height) continue; // we want TOP nodes (higher Y)
      const wx = p.x + n.x;
      const wy = p.y + n.y;
      if (!best || wy > best.wy) best = { wx, wy };
    }
  }
  return best ?? { wx: 0, wy: 0 };
}

/** Lowest world point of any attached stack node (where an engine would go). */
function lowestStackPoint(design: RocketDesign, partDef: (id: string) => PartDef) {
  let best: { wx: number; wy: number } | undefined;
  for (const p of design.parts) {
    const d = partDef(p.partId);
    for (const n of d.nodes) {
      if (n.kind !== "stack") continue;
      if (n.y > 0.5 * d.height) continue; // we want BOTTOM nodes (lower Y)
      const wx = p.x + n.x;
      const wy = p.y + n.y;
      if (!best || wy < best.wy) best = { wx, wy };
    }
  }
  return best ?? { wx: 0, wy: 0 };
}

export function newDesign(name = "New Rocket"): RocketDesign {
  return {
    id: crypto.randomUUID(),
    name,
    updatedAt: Date.now(),
    parts: [],
  };
}

export function totalMass(design: RocketDesign, partDef: (id: string) => PartDef) {
  let m = 0;
  for (const p of design.parts) {
    const def = partDef(p.partId);
    m += def.mass + (p.fuel ?? def.fuelCapacity ?? 0);
  }
  return m;
}

/** Highest part stage number → number of stages (activation is in ascending order). */
export function stageCount(design: RocketDesign) {
  if (design.parts.length === 0) return 0;
  return Math.max(...design.parts.map((p) => p.stage)) + 1;
}

/** Sum sea-level thrust available at a given stage. */
export function thrustAtStage(
  design: RocketDesign,
  stage: number,
  partDef: (id: string) => PartDef,
  altitude = 0,
) {
  let t = 0;
  for (const p of design.parts) {
    if (p.stage !== stage) continue;
    const def = partDef(p.partId);
    if (!def.thrust) continue;
    if (p.fuel !== undefined && p.fuel <= 0) continue;
    t += def.thrust * aslFactor(def, altitude);
  }
  return t;
}

/** Extremely rough ASL → Vac lerp using an exponential atmosphere. */
export function aslFactor(def: PartDef, altitudeM: number) {
  if (!def.thrust) return 1;
  const atm = Math.exp(-altitudeM / 7500);
  const asl = def.ispAsl ?? def.ispVac ?? 280;
  const vac = def.ispVac ?? asl;
  return (atm * asl + (1 - atm) * vac) / vac;
}
